import {
  FunctionComponent,
  useState,
  useReducer,
  useEffect,
  useRef,
} from "react";
import TextField, { TextFieldProps } from "./TextField";
import CheckBox, { CheckBoxProps } from "../CheckBox/CheckBox";
import Button from "../Button/Button";
import Column from "../Column/Column";
import RadioButtonGroup, {
  RadioButtonGroupType,
} from "../Radio/RadioButtonGroup";
import FileUploadField, {
  FileUploadType,
} from "../FileUploadField/FileUploadField";

export interface TextFieldsData {
  id: string;
  title: string;
  heading: string;
  description: string;
  encrypt?: boolean;
  addToScore?: boolean;
  type: string;
  validations: {
    required?: boolean;
  };
  components: Array<any>;
  properties: {
    allow_multiple_selection: boolean;
    allow_other_choice: boolean;
    randomize: boolean;
    vertical_alignment: boolean;
  };
}

type TextFieldsProps = {
  data: TextFieldsData;
  onDataSelected: Function;
  onBack?: Function;
  errors: { [key: string]: String };
  isActive: boolean;
  nextPage?: string;
  isLoading?: boolean;
};

const TextFields: FunctionComponent<TextFieldsProps> = ({
  data,
  onDataSelected,
  onBack,
  errors,
  isActive,
  nextPage,
  isLoading,
}) => {
  let initialState: { [key: string]: any } = {};
  data.components.forEach((input) => {
    const id: any = input.id;
    initialState[id] = "";
  });
  const [formInput, setFormInput] = useReducer(
    (state: any, newState: any) => ({ ...state, ...newState }),
    initialState
  );
  const ref = useRef(formInput);

  useEffect(() => {
    ref.current = formInput;
  }, [formInput]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener("keydown", onContinue);
      return () => {
        document.removeEventListener("keydown", onContinue);
      };
    }
    return;
  }, [isActive]);

  const onContinue = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      onSelectData();
    }
  };

  const formattedInput = (value: { [key: string]: any }) => {
    const inputArray = Object.keys(value).map((id) => {
      const label = data.components.find((el) => el.id === id).label;
      return { id, value: value[id], label };
    });
    return inputArray;
  };

  const onInputChange = (value: string | boolean, id: string) => {
    if (value === true) {
      setFormInput({ [id]: "accepted" });
    } else if (value === false) {
      setFormInput({ [id]: false });
    } else {
      setFormInput({ [id]: value });
    }
  };

  const content: JSX.Element[] = [];
  data.components.forEach((el) => {
    let element;
    if (el.type === "checkbox") {
      const checkboxData: CheckBoxProps = el;
      element = (
        <CheckBox
          {...checkboxData}
          key={el.id}
          setChecked={onInputChange}
          error={!!errors[el.id]}
          errorText={errors[el.id]}
        />
      );
    } else if (el.type === "file") {
      const fileUploadData: FileUploadType = el;
      element = (
        <FileUploadField
          {...fileUploadData}
          setFile={onInputChange}
          error={!!errors[el.id]}
          errorText={errors[el.id]}
        />
      );
    } else if (el.type === "radio") {
      const radioButtonData: RadioButtonGroupType = el;
      element = (
        <RadioButtonGroup
          {...radioButtonData}
          key={el.id}
          active={formInput[el.id]}
          setValue={onInputChange}
          error={!!errors[el.id]}
          errorText={errors[el.id]}
        />
      );
    } else {
      const textfieldProps: TextFieldProps = el;
      element = (
        <TextField
          {...textfieldProps}
          key={el.id}
          onChange={onInputChange}
          error={!!errors[el.id]}
          errorText={errors[el.id]}
        />
      );
    }
    content[el.index] = (
      <div className="mb-6" key={el.id}>
        {element}
      </div>
    );
  });

  const onSelectData = () => {
    let answer: { [key: string]: any } = {};

    formattedInput(ref.current).forEach((el) => {
      answer[el.id] = el;
    });
    const selectedData = {
      type: "input",
      page: { type: "input", id: data.id },
      data: answer,
    };
    onDataSelected(selectedData);
  };
  return (
    <div className="w-full max-w-3xl">
      {content}
      <div className="flex flex-wrap justify-center mb-12 -mx-2 lg:-mx-4">
        {onBack ? (
          <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
            <Button variant="outline" onPress={() => onBack()}>
              Zurück
            </Button>
          </div>
        ) : null}
        <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
          <Button
            variant="primary"
            onPress={() => onSelectData()}
            isLoading={isLoading}
          >
            {nextPage ? "Weiter" : "Anmelden"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TextFields;
